import type { GetServerSideProps, GetStaticProps, InferGetServerSidePropsType } from 'next';
import { NextSeo } from 'next-seo';
import type { ReactElement } from 'react';
import React from 'react';

import { Container, Main } from '@seuk/design-system/atoms';
import { Breadcrumbs } from '@seuk/design-system/molecules';

import { Hero } from '../../components';
import { HeroDetails } from '../../components/hero/hero-details';
import { MarketingSection } from '../../components/marketing/marketing-section';

import ArticlesFinder from './components/articles-finder';
import type { EnergyInsightsPageProps } from './types';

const BREADCRUMBS = [{ label: 'Home', href: '/' }, { label: 'Energy insights' }];
function EnergyInsightsPage<T extends GetStaticProps<EnergyInsightsPageProps>>({
  seo,
  hero,
  articles,
  articleFinder,
  sections,
}: InferGetServerSidePropsType<T>): ReactElement {
  return (
    <>
      <NextSeo title={seo?.title} description={seo?.description} noindex={seo?.noindex} nofollow={seo?.nofollow} />
      <Main data-testid="home-page">
        <Container>
          <Breadcrumbs items={BREADCRUMBS} backHref={BREADCRUMBS[0].href} />
        </Container>
        <Hero heroImage={hero.image} heroAltText={hero.imageAlt} bgAlign={hero.imageAlignment} id="hero">
          {hero.title && <HeroDetails title={hero.title} subtitle={hero.subtitle} link={hero.link} />}
        </Hero>

        {articleFinder && <ArticlesFinder {...{ articleFinder, articles }} />}

        {sections?.map((section) => (
          <MarketingSection key={section.id} {...section} />
        ))}
      </Main>
    </>
  );
}

export function makeEnergyInsightsPage(): React.FC<
  InferGetServerSidePropsType<GetServerSideProps<EnergyInsightsPageProps>>
> {
  return function CustomEnergyInsightsPage(props) {
    return <EnergyInsightsPage {...props} />;
  };
}
